import { UserStatus } from '../../../../types/UserStatus';
import { RadioCardGroupOption } from '../../../cdl/RadioCard/RadioCardGroup';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';

import { useMemberQuickFilterTotals } from './useMemberQuickFilterTotals';

export enum MemberQuickFilter {
    ALL = 'all',
    ACTIVE = 'active',
    PENDING = 'pending',
    INACTIVE = 'inactive',
}

export type MemberQuickFilterType = MemberQuickFilter;

interface UseMemberQuickFilterParams {
    searchQuery?: string;
    companyIds?: string[];
    groupIds?: string[];
    currentStatus?: UserStatus;
    onStatusChange: (status: UserStatus | undefined) => void;
}

interface UseMemberQuickFilterResult {
    selectedFilter: MemberQuickFilterType;
    setSelectedFilter: (filter: MemberQuickFilterType) => void;
    filterOptions: RadioCardGroupOption<MemberQuickFilterType>[];
}

const statusToFilterType = (status: UserStatus | undefined): MemberQuickFilterType => {
    switch (status) {
        case UserStatus.ACTIVE:
            return MemberQuickFilter.ACTIVE;
        case UserStatus.INVITED:
            return MemberQuickFilter.PENDING;
        case UserStatus.INACTIVE:
            return MemberQuickFilter.INACTIVE;
        default:
            return MemberQuickFilter.ALL;
    }
};

const filterTypeToStatus = (filterType: MemberQuickFilterType): UserStatus | undefined => {
    switch (filterType) {
        case MemberQuickFilter.ACTIVE:
            return UserStatus.ACTIVE;
        case MemberQuickFilter.PENDING:
            return UserStatus.INVITED;
        case MemberQuickFilter.INACTIVE:
            return UserStatus.INACTIVE;
        default:
            return undefined;
    }
};

export const useMemberQuickFilter = ({
    searchQuery,
    companyIds,
    groupIds,
    currentStatus,
    onStatusChange,
}: UseMemberQuickFilterParams): UseMemberQuickFilterResult => {
    const { getHumanReadableValue } = useEnums();
    const selectedFilter = statusToFilterType(currentStatus);

    const totals = useMemberQuickFilterTotals({
        searchQuery,
        companyIds,
        groupIds,
    });

    const setSelectedFilter = (filter: MemberQuickFilterType) => {
        const newStatus = filterTypeToStatus(filter);
        onStatusChange(newStatus);
    };

    const filterOptions: RadioCardGroupOption<MemberQuickFilterType>[] = [
        {
            value: MemberQuickFilter.ALL,
            primaryLabel: translate('members.filters.allMembers'),
            secondaryLabel: totals.all.toString(),
        },
        {
            value: MemberQuickFilter.ACTIVE,
            primaryLabel: getHumanReadableValue('status', UserStatus.ACTIVE),
            secondaryLabel: totals.active.toString(),
        },
        {
            value: MemberQuickFilter.PENDING,
            primaryLabel: getHumanReadableValue('status', UserStatus.INVITED),
            secondaryLabel: totals.pending.toString(),
        },
        {
            value: MemberQuickFilter.INACTIVE,
            primaryLabel: getHumanReadableValue('status', UserStatus.INACTIVE),
            secondaryLabel: totals.inactive.toString(),
        },
    ];

    return {
        selectedFilter,
        setSelectedFilter,
        filterOptions,
    };
};
